import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface Notification {
  id: string;
  type: 'alert' | 'info' | 'warning' | 'success';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  data?: {
    alertId?: string;
    deviceId?: string;
    deviceName?: string;
    severity?: string;
    [key: string]: unknown;
  };
}

export type NotificationSoundType = 'default' | 'chime' | 'bell' | 'alert' | 'none';

export interface NotificationPreferences {
  alerts: boolean;
  warnings: boolean;
  info: boolean;
  success: boolean;
  overspeed: boolean;
  geofence: boolean;
  offline: boolean;
  battery: boolean;
  sos: boolean;
  maintenance: boolean;
}

interface NotificationsState {
  notifications: Notification[];
  unreadCount: number;
  soundEnabled: boolean;
  soundType: NotificationSoundType;
  pushEnabled: boolean;
  pushPermission: NotificationPermission | 'unsupported';
  isOpen: boolean;
  preferences: NotificationPreferences;

  // Actions
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
  toggleSound: () => void;
  setSoundType: (type: NotificationSoundType) => void;
  setIsOpen: (isOpen: boolean) => void;
  requestPushPermission: () => Promise<boolean>;
  setPushEnabled: (enabled: boolean) => void;
  updatePreferences: (prefs: Partial<NotificationPreferences>) => void;
  checkPushPermission: () => void;
}

// Check if browser supports notifications
const supportsNotifications = 'Notification' in window;

export const useNotificationsStore = create<NotificationsState>()(
  persist(
    (set, get) => ({
      notifications: [],
      unreadCount: 0,
      soundEnabled: true,
      soundType: 'default',
      pushEnabled: false,
      pushPermission: supportsNotifications ? Notification.permission : 'unsupported',
      isOpen: false,
      preferences: {
        alerts: true,
        warnings: true,
        info: true,
        success: true,
        overspeed: true,
        geofence: true,
        offline: true,
        battery: true,
        sos: true,
        maintenance: true,
      },

      addNotification: (notification) => {
        const state = get();

        // Check if notification type is enabled in preferences
        const typeKey = notification.type === 'alert' ? 'alerts' : notification.type === 'warning' ? 'warnings' : notification.type;
        if (!state.preferences[typeKey as keyof typeof state.preferences]) return;

        const newNotification: Notification = {
          ...notification,
          id: `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          timestamp: new Date(),
          read: false,
        };

        set((s) => ({
          notifications: [newNotification, ...s.notifications].slice(0, 50),
          unreadCount: s.unreadCount + 1,
        }));

        // Play sound if enabled
        if (state.soundEnabled && state.soundType !== 'none') {
          playNotificationSound(state.soundType, notification.type);
        }

        // Show browser push notification if enabled
        if (state.pushEnabled && state.pushPermission === 'granted') {
          showPushNotification(newNotification);
        }
      },

      markAsRead: (id) => {
        set((state) => {
          const notification = state.notifications.find((n) => n.id === id);
          if (!notification || notification.read) return state;

          return {
            notifications: state.notifications.map((n) =>
              n.id === id ? { ...n, read: true } : n
            ),
            unreadCount: Math.max(0, state.unreadCount - 1),
          };
        });
      },

      markAllAsRead: () => {
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, read: true })),
          unreadCount: 0,
        }));
      },

      removeNotification: (id) => {
        set((state) => {
          const notification = state.notifications.find((n) => n.id === id);
          return {
            notifications: state.notifications.filter((n) => n.id !== id),
            unreadCount: notification && !notification.read
              ? Math.max(0, state.unreadCount - 1)
              : state.unreadCount,
          };
        });
      },

      clearAll: () => {
        set({ notifications: [], unreadCount: 0 });
      },

      toggleSound: () => {
        set((state) => ({ soundEnabled: !state.soundEnabled }));
      },

      setSoundType: (type) => {
        set({ soundType: type });
        // Play preview sound
        if (type !== 'none') {
          playNotificationSound(type, 'info');
        }
      },

      setIsOpen: (isOpen) => {
        set({ isOpen });
      },

      requestPushPermission: async () => {
        if (!supportsNotifications) {
          set({ pushPermission: 'unsupported' });
          return false;
        }

        try {
          const permission = await Notification.requestPermission();
          set({ pushPermission: permission, pushEnabled: permission === 'granted' });
          return permission === 'granted';
        } catch {
          set({ pushPermission: 'denied' });
          return false;
        }
      },

      setPushEnabled: (enabled) => {
        const state = get();
        if (enabled && state.pushPermission !== 'granted') {
          // Need to request permission first
          get().requestPushPermission();
        } else {
          set({ pushEnabled: enabled });
        }
      },

      updatePreferences: (prefs) => {
        set((state) => ({
          preferences: { ...state.preferences, ...prefs },
        }));
      },

      checkPushPermission: () => {
        if (supportsNotifications) {
          set({ pushPermission: Notification.permission });
        }
      },
    }),
    {
      name: 'trackfleet-notifications',
      partialize: (state) => ({
        soundEnabled: state.soundEnabled,
        soundType: state.soundType,
        pushEnabled: state.pushEnabled,
        preferences: state.preferences,
      }),
    }
  )
);

// Sound frequencies for different sounds
const SOUND_CONFIGS: Record<NotificationSoundType, { frequencies: number[]; durations: number[] }> = {
  default: { frequencies: [800], durations: [200] },
  chime: { frequencies: [523, 659, 784], durations: [100, 100, 200] },
  bell: { frequencies: [440, 550], durations: [150, 150] },
  alert: { frequencies: [880, 660, 880], durations: [100, 100, 200] },
  none: { frequencies: [], durations: [] },
};

// Different volumes for notification types
const TYPE_VOLUMES: Record<Notification['type'], number> = {
  alert: 0.5,
  warning: 0.4,
  info: 0.3,
  success: 0.3,
};

function playNotificationSound(soundType: NotificationSoundType, notifType: Notification['type']) {
  if (soundType === 'none') return;

  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const config = SOUND_CONFIGS[soundType];
    const volume = TYPE_VOLUMES[notifType];

    let startTime = audioContext.currentTime;

    config.frequencies.forEach((freq, index) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = freq;
      oscillator.type = soundType === 'alert' ? 'square' : 'sine';

      const duration = config.durations[index] / 1000;

      gainNode.gain.setValueAtTime(volume, startTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

      oscillator.start(startTime);
      oscillator.stop(startTime + duration);

      startTime += duration;
    });
  } catch {
    // Audio not available, fail silently
  }
}

function showPushNotification(notification: Notification) {
  if (!supportsNotifications || Notification.permission !== 'granted') return;

  try {
    new Notification(notification.title, {
      body: notification.message,
      icon: '/trackfleet-icon.png',
      badge: '/trackfleet-badge.png',
      tag: notification.id,
      requireInteraction: notification.type === 'alert',
      silent: true, // We handle sound ourselves
      data: notification.data,
    });
  } catch {
    // Push notification failed, fail silently
  }
}

// Helper function to create notification from alert
export function createNotificationFromAlert(alert: any): Omit<Notification, 'id' | 'timestamp' | 'read'> | null {
  const store = useNotificationsStore.getState();

  // Check alert type preferences
  const alertTypePrefs: Record<string, keyof NotificationPreferences> = {
    OVERSPEED: 'overspeed',
    GEOFENCE_ENTER: 'geofence',
    GEOFENCE_EXIT: 'geofence',
    DEVICE_OFFLINE: 'offline',
    LOW_BATTERY: 'battery',
    SOS: 'sos',
    MAINTENANCE_DUE: 'maintenance',
  };

  const prefKey = alertTypePrefs[alert.type];
  if (prefKey && !store.preferences[prefKey]) {
    return null;
  }

  const typeMap: Record<string, { title: string; type: Notification['type'] }> = {
    OVERSPEED: { title: 'Excès de vitesse', type: 'warning' },
    GEOFENCE_ENTER: { title: 'Entrée dans zone', type: 'info' },
    GEOFENCE_EXIT: { title: 'Sortie de zone', type: 'info' },
    DEVICE_OFFLINE: { title: 'Véhicule hors ligne', type: 'warning' },
    LOW_BATTERY: { title: 'Batterie faible', type: 'warning' },
    SOS: { title: 'Alerte SOS', type: 'alert' },
    HARSH_BRAKING: { title: 'Freinage brusque', type: 'warning' },
    HARSH_ACCELERATION: { title: 'Accélération brusque', type: 'warning' },
    MAINTENANCE_DUE: { title: 'Maintenance requise', type: 'info' },
  };

  const config = typeMap[alert.type] || { title: 'Nouvelle alerte', type: 'info' as const };

  let message = alert.message || '';
  if (!message) {
    switch (alert.type) {
      case 'OVERSPEED':
        message = `Vitesse: ${alert.data?.speed || '?'} km/h`;
        break;
      case 'GEOFENCE_ENTER':
        message = `Zone: ${alert.data?.geofenceName || 'Inconnue'}`;
        break;
      case 'GEOFENCE_EXIT':
        message = `Zone: ${alert.data?.geofenceName || 'Inconnue'}`;
        break;
      case 'LOW_BATTERY':
        message = `Niveau: ${alert.data?.battery || '?'}%`;
        break;
      default:
        message = alert.type;
    }
  }

  return {
    type: config.type,
    title: config.title,
    message: `${alert.device?.name || 'Véhicule'} - ${message}`,
    data: {
      alertId: alert.id,
      deviceId: alert.deviceId,
      deviceName: alert.device?.name,
      severity: alert.severity,
    },
  };
}

// Helper function to create notification from mission event
export function createNotificationFromMissionEvent(
  event: { type: string; mission: Record<string, unknown> }
): Omit<Notification, 'id' | 'timestamp' | 'read'> | null {
  const mission = event.mission;
  const ref = (mission.reference as string) || '';
  const title = (mission.title as string) || '';

  if (event.type === 'assigned') {
    const scheduledAt = mission.scheduledAt
      ? new Date(mission.scheduledAt as string).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
      : '';
    return {
      type: 'info',
      title: `Nouvelle mission : ${ref}`,
      message: `${title}${scheduledAt ? ` — ${scheduledAt}` : ''}`,
      data: { missionId: mission.id as string },
    };
  }

  if (event.type === 'updated') {
    const scheduledAt = mission.scheduledAt
      ? new Date(mission.scheduledAt as string).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
      : '';
    return {
      type: 'info',
      title: `Mission modifiée : ${ref}`,
      message: `${title}${scheduledAt ? ` — ${scheduledAt}` : ''}`,
      data: { missionId: mission.id as string },
    };
  }

  if (event.type === 'completed') {
    const driverName = (mission.driverName as string) || 'Un conducteur';
    return {
      type: 'success',
      title: `Mission terminée : ${ref}`,
      message: `${driverName} a terminé "${title}"`,
      data: { missionId: mission.id as string },
    };
  }

  return null;
}
